import { buildMatchingContext } from "./matchingContext";
import { evaluateRule } from "./ruleEvaluator";

const configuredRules = (question) => (
  question?.visibility_rules
  || question?.visibilityRules
  || question?.conditional_logic
  || {}
);

export const isTreatmentQuestionVisible = (
  question,
  questionnaire,
  responses,
) => {
  const rules = configuredRules(question);
  if (!rules || typeof rules !== "object" || !Object.keys(rules).length) {
    return true;
  }
  const context = buildMatchingContext(questionnaire, responses, question);
  if (rules.skip_if && evaluateRule(rules.skip_if, context)) {
    return false;
  }
  if (rules.show_if) {
    return evaluateRule(rules.show_if, context);
  }
  return evaluateRule(rules, context);
};

const responseIdentities = (question) => [
  question?.id,
  question?.source_id,
  question?.release_node_id,
].filter((identity) => identity !== undefined && identity !== null && identity !== "")
  .map(String);

const isProgramScreeningQuestion = (question) => (
  Boolean(question?.program_id)
  && !["product_selection", "treatment_screening_intro"].includes(
    String(question?.question_type || question?.kind || "").toLowerCase(),
  )
);

export const pruneNewlyHiddenProgramResponses = (
  questionnaire,
  previousResponses,
  nextResponses,
) => {
  // This helper is intentionally transition-aware. Call it only for a direct
  // response edit with the complete before/after snapshots; restore and
  // prefill paths must not infer that an already-hidden answer is stale.
  const previouslyVisibleQuestions = (questionnaire?.questions || [])
    .filter(isProgramScreeningQuestion)
    .filter((question) => isTreatmentQuestionVisible(
      question,
      questionnaire,
      previousResponses,
    ));
  const reconciled = { ...(nextResponses || {}) };

  for (
    let iteration = 0;
    iteration <= previouslyVisibleQuestions.length;
    iteration += 1
  ) {
    const newlyHiddenAnsweredQuestions = previouslyVisibleQuestions.filter(
      (question) => (
        responseIdentities(question).some((identity) => (
          Object.prototype.hasOwnProperty.call(reconciled, identity)
        ))
        && !isTreatmentQuestionVisible(question, questionnaire, reconciled)
      ),
    );
    if (!newlyHiddenAnsweredQuestions.length) return reconciled;
    newlyHiddenAnsweredQuestions.forEach((question) => {
      responseIdentities(question).forEach((identity) => {
        delete reconciled[identity];
      });
    });
  }

  return reconciled;
};

export const projectVisibleProgramResponses = (questionnaire, responses) => {
  // Checkpoint-only projection: filtering this clone must never mutate or be
  // persisted over the browser's response state.
  const questions = (questionnaire?.questions || []).filter(
    isProgramScreeningQuestion,
  );
  const reconciled = { ...(responses || {}) };

  for (let iteration = 0; iteration <= questions.length; iteration += 1) {
    const hiddenAnsweredQuestions = questions.filter((question) => (
      responseIdentities(question).some((identity) => (
        Object.prototype.hasOwnProperty.call(reconciled, identity)
      ))
      && !isTreatmentQuestionVisible(question, questionnaire, reconciled)
    ));
    if (!hiddenAnsweredQuestions.length) return reconciled;
    hiddenAnsweredQuestions.forEach((question) => {
      responseIdentities(question).forEach((identity) => {
        delete reconciled[identity];
      });
    });
  }

  return reconciled;
};
